import {
  SnowflakeSSEEvent,
  SnowflakeSSEEventType,
  SnowflakeStatusEvent,
  SnowflakeTextDeltaEvent,
  SnowflakeThinkingDeltaEvent,
  SnowflakeToolUseEvent,
  SnowflakeToolResultEvent,
  SnowflakeTableEvent,
  SnowflakeChartEvent,
  SnowflakeErrorEvent,
  SnowflakeMetadataEvent,
  SnowflakeResponseEvent
} from './types'

/**
 * Parses a raw SSE buffer into structured events.
 *
 * Uses `\n\n` event boundaries to ensure only fully-received events are parsed.
 * This prevents data loss when large payloads (e.g. response.table with thousands
 * of rows) arrive split across multiple TCP chunks.
 *
 * SSE Format from Snowflake:
 * ```
 * event: response.status
 * data: {"message":"Planning the next steps","status":"planning"}
 *
 * event: response.text.delta
 * data: {"content_index":0,"text":"Hello"}
 * ```
 */
export function parseSSEEvents(buffer: string): {
  events: SnowflakeSSEEvent[]
  remainingBuffer: string
} {
  const events: SnowflakeSSEEvent[] = []

  // Only parse up to the last complete event boundary (\n\n)
  const lastBoundary = buffer.lastIndexOf('\n\n')
  if (lastBoundary === -1) {
    // No complete events yet — keep buffering
    return { events, remainingBuffer: buffer }
  }

  const completePart = buffer.substring(0, lastBoundary)
  const remainingBuffer = buffer.substring(lastBoundary + 2)

  // Split into individual event blocks
  const blocks = completePart.split('\n\n')

  for (const block of blocks) {
    if (!block.trim()) continue

    let eventType: SnowflakeSSEEventType | null = null
    let dataLine: string | null = null

    for (const line of block.split('\n')) {
      if (line.startsWith('event: ')) {
        eventType = line.slice(7).trim() as SnowflakeSSEEventType
      } else if (line.startsWith('data: ')) {
        dataLine = line.slice(6)
      }
    }

    if (!eventType || dataLine === null) continue

    // Handle [DONE] marker
    if (dataLine === '[DONE]') {
      events.push({ event: eventType, data: { done: true } })
      continue
    }

    try {
      events.push({ event: eventType, data: JSON.parse(dataLine) })
    } catch (e) {
      console.warn('Failed to parse SSE data for event:', eventType, e)
    }
  }

  return { events, remainingBuffer }
}

/**
 * Type guard for status events
 */
export function isStatusEvent(event: SnowflakeSSEEvent): event is SnowflakeSSEEvent & { data: SnowflakeStatusEvent } {
  return event.event === 'response.status'
}

/**
 * Type guard for text delta events
 */
export function isTextDeltaEvent(
  event: SnowflakeSSEEvent
): event is SnowflakeSSEEvent & { data: SnowflakeTextDeltaEvent } {
  return event.event === 'response.text.delta'
}

/**
 * Type guard for thinking delta events
 */
export function isThinkingDeltaEvent(
  event: SnowflakeSSEEvent
): event is SnowflakeSSEEvent & { data: SnowflakeThinkingDeltaEvent } {
  return event.event === 'response.thinking.delta'
}

/**
 * Type guard for tool use events
 */
export function isToolUseEvent(event: SnowflakeSSEEvent): event is SnowflakeSSEEvent & { data: SnowflakeToolUseEvent } {
  return event.event === 'response.tool_use'
}

/**
 * Type guard for tool result events
 */
export function isToolResultEvent(
  event: SnowflakeSSEEvent
): event is SnowflakeSSEEvent & { data: SnowflakeToolResultEvent } {
  return event.event === 'response.tool_result'
}

/**
 * Type guard for table events (contains result_set with query data)
 */
export function isTableEvent(event: SnowflakeSSEEvent): event is SnowflakeSSEEvent & { data: SnowflakeTableEvent } {
  return event.event === 'response.table'
}

/**
 * Type guard for chart events (contains vega-lite chart_spec)
 */
export function isChartEvent(event: SnowflakeSSEEvent): event is SnowflakeSSEEvent & { data: SnowflakeChartEvent } {
  return event.event === 'response.chart'
}

/**
 * Type guard for error events
 */
export function isErrorEvent(event: SnowflakeSSEEvent): event is SnowflakeSSEEvent & { data: SnowflakeErrorEvent } {
  return event.event === 'error'
}

/**
 * Type guard for done events
 */
export function isDoneEvent(event: SnowflakeSSEEvent): boolean {
  return event.event === 'done'
}

/**
 * Type guard for response events (contains thread_id and message_id)
 */
export function isResponseEvent(
  event: SnowflakeSSEEvent
): event is SnowflakeSSEEvent & { data: SnowflakeResponseEvent } {
  return event.event === 'response'
}

/**
 * Type guard for metadata events (contains role and message_id)
 */
export function isMetadataEvent(
  event: SnowflakeSSEEvent
): event is SnowflakeSSEEvent & { data: SnowflakeMetadataEvent } {
  return event.event === 'metadata'
}
